/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import type { Language } from '../contexts/LanguageContext';

// BCP-47 tag per UI language. Centralised so the EN locale (en-GB — European
// English: period decimal, comma thousands) and NL locale (nl-NL — comma
// decimal, period thousands, space-prefixed currency) can be tuned in one place.
export const LOCALE_MAP: Record<Language, string> = {
  nl: 'nl-NL',
  en: 'en-GB',
};

export const toLocaleTag = (lang: Language): string => LOCALE_MAP[lang];

// Every user-facing date renders in the PLATFORM's timezone, never the
// viewer's. The pages that show dates are prerendered: the HTML is baked at
// boot on Render (UTC) and hydrated in the visitor's own timezone, so a
// formatter that follows the runtime zone yields a different calendar day for
// anyone west of UTC — a text hydration mismatch (React #418) that makes React
// throw the whole SSR tree away. A date-only string is the sharpest case:
// `new Date('2026-08-19')` is UTC midnight, so it renders "18 aug" in every
// American timezone. Same Amsterdam-day convention as the server's per-day
// counters (server/data/dayKeys.ts).
export const SITE_TIME_ZONE = 'Europe/Amsterdam';

// The month NAMES are the site's own table, not ICU's. What Intl prints for a
// locale differs per engine and ICU build — Apple's ICU abbreviates Dutch
// months with a trailing period ("26 apr. 2026") where V8, SpiderMonkey and
// Node print "26 apr 2026" — and on a prerendered page that difference is a
// text hydration mismatch (React #418): the bake carries Node's spelling, the
// visitor's browser disagrees, React discards the whole SSR tree (seen on
// /nl/kennisbank, 2026-09-01). Intl still does the one thing every build does
// identically: resolve an instant to its calendar day in SITE_TIME_ZONE. The
// English abbreviations follow en-GB CLDR ("Sept"), so the baked HTML stays
// byte-identical to what Node produced before this table existed.
const MONTH_NAMES: Record<Language, Record<'short' | 'long', readonly string[]>> = {
  nl: {
    short: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
    long: [
      'januari',
      'februari',
      'maart',
      'april',
      'mei',
      'juni',
      'juli',
      'augustus',
      'september',
      'oktober',
      'november',
      'december',
    ],
  },
  en: {
    short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
    long: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
  },
};

export interface SiteDateParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
}

// Numeric fields only, en-US digits: the one Intl output that no engine
// localises differently.
let partsFormatter: Intl.DateTimeFormat | undefined;

/** Calendar day of `value` in SITE_TIME_ZONE, or null for unparseable input. */
export const siteDateParts = (value: string | Date): SiteDateParts | null => {
  const date = value instanceof Date ? value : new Date(value);
  // Unparseable input yields null so callers can fall back to the raw string
  // instead of rendering "Invalid Date" — Intl throws on such a Date.
  if (Number.isNaN(date.getTime())) return null;
  if (!partsFormatter) {
    partsFormatter = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      timeZone: SITE_TIME_ZONE,
    });
  }
  const parts = partsFormatter.formatToParts(date);
  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: field('year'), month: field('month'), day: field('day') };
};

const formatDate = (value: string | Date, lang: Language, month: 'short' | 'long'): string => {
  const p = siteDateParts(value);
  if (!p) return '';
  return `${p.day} ${MONTH_NAMES[lang][month][p.month - 1]} ${p.year}`;
};

/** "19 aug 2026" / "19 Aug 2026" — timezone-pinned, see SITE_TIME_ZONE. */
export const formatDateShort = (value: string | Date, lang: Language): string =>
  formatDate(value, lang, 'short');

/** "19 augustus 2026" / "19 August 2026" — timezone-pinned, see SITE_TIME_ZONE. */
export const formatDateLong = (value: string | Date, lang: Language): string =>
  formatDate(value, lang, 'long');

/** "augustus 2026" / "August 2026" — month and year only, same pin and table. */
export const formatMonthYear = (value: string | Date, lang: Language): string => {
  const p = siteDateParts(value);
  if (!p) return '';
  return `${MONTH_NAMES[lang].long[p.month - 1]} ${p.year}`;
};

/**
 * One month's name from the same table: "oktober" / "October", "okt" / "Oct" —
 * what a month picker lists. '' for a number that is no month.
 */
export const formatMonthName = (
  month: number,
  lang: Language,
  style: 'long' | 'short' = 'long'
): string =>
  Number.isInteger(month) && month >= 1 && month <= 12 ? MONTH_NAMES[lang][style][month - 1] : '';

// Days per month of a leap year: a yearless day such as 29 February is a day.
const MONTH_LENGTHS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/**
 * A yearless calendar day — the dossier's `month_day` unit, MMDD as an integer
 * (`1001` = 1 October) — in the site's own month names: "1 oktober" / "1 okt",
 * "1 October" / "1 Oct". No Intl, no timezone, no year: the same string in the
 * bake and in every browser (the MONTH_NAMES rule above). '' for an integer
 * that names no day, so a bad value never prints as "1.001".
 */
export const formatMonthDay = (
  monthDay: number,
  lang: Language,
  style: 'long' | 'short' = 'long'
): string => {
  if (!Number.isInteger(monthDay)) return '';
  const month = Math.floor(monthDay / 100);
  const day = monthDay % 100;
  if (month < 1 || month > 12 || day < 1 || day > MONTH_LENGTHS[month - 1]) return '';
  return `${day} ${MONTH_NAMES[lang][style][month - 1]}`;
};

// Building an Intl.NumberFormat is the expensive part of formatting a number,
// and every row formats every figure on every render: ~0.3 s of main thread on
// a phone profile of the homepage at 4x CPU slowdown (2026-10-02). One instance
// per language + options, reused; the options objects are small and few.
const numberFormats = new Map<string, Intl.NumberFormat>();
const numberFormat = (lang: Language, opts?: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = opts ? `${lang}|${JSON.stringify(opts)}` : lang;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(LOCALE_MAP[lang], opts);
    numberFormats.set(key, format);
  }
  return format;
};

export const formatNumber = (n: number, lang: Language, opts?: Intl.NumberFormatOptions): string =>
  numberFormat(lang, opts).format(n);

export const formatDecimal = (n: number, lang: Language, fractionDigits = 1): string =>
  formatNumber(n, lang, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

export const formatInteger = (n: number, lang: Language): string =>
  formatNumber(n, lang, { maximumFractionDigits: 0 });

// Fraction digits for a euro amount: cents only when the amount has them —
// "€ 72" but "€ 71,99". Pass the result as formatCurrencyEUR's fractionDigits.
export const eurDecimals = (n: number): number => (Number.isInteger(n) ? 0 : 2);

export const formatCurrencyEUR = (n: number, lang: Language, fractionDigits = 0): string =>
  numberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);

/**
 * A PRICE — a tariff, a surcharge, an invoice line — always with both cents.
 * `formatCurrencyEUR` defaults to 0 fraction digits, which prints a € 2,50
 * toeslag as "€ 3" on the very screens where a provider agrees to what they
 * will be invoiced. Every price surface (the portal's afspraak card, the admin
 * aanmeldingen inbox, the CRM panel) goes through this one wrapper instead of
 * repeating the `, 2`.
 */
export const formatEuroPrice = (n: number, lang: Language): string => formatCurrencyEUR(n, lang, 2);

/**
 * The ERE rekenwaarde at ITS OWN precision — "€ 0,445", "€ 0,49", "€ 0,4858":
 * never fewer than two decimals and never more than four (a reading is capped
 * at four in normalizeErePriceEntry). Two readings a week apart, 0,449 and
 * 0,445, both printed as "€ 0,45" beside a "0,9% lager" change line, which
 * is the contradiction this ends; and a worked example that prints the
 * rounded price next to a gross computed on the full one does not multiply
 * out. Every surface that names the price goes through this one function.
 */
export const formatErePrice = (price: number, lang: Language): string => {
  const text = String(price);
  const dot = text.indexOf('.');
  const decimals = dot < 0 ? 0 : text.length - dot - 1;
  return formatCurrencyEUR(price, lang, Math.min(4, Math.max(2, decimals)));
};
